import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { IResourceDatabaseRecord, IResourceFavoriteRequest, IResourceItemType } from '@/api/types';
import type { RootState } from '@/store';
import type { IResourceType } from '@/components/common';
import type { ISelectedFolder } from '@/store/types';

import { CrossIcon, Star, StarEmpty } from '@/assets/icons';
import { ButtonBack } from '@/components/common';
import { getResourceTypeId } from '@/helpers';
import { colors, otherColors } from '@/styles/colors';
import { media } from '@/styles/theme';
import { MONTH_DAY_YEAR_HOUR_FORMAT } from '@/config/variables';
import { ResourceTypeId, UserType } from '@/store/enums';
import { useAppDispatch, useAppSelector, useResourceNavigation, useTimezone } from '@/hooks';
import { getFavoritesList, removeFavorite, setFavorite } from '@/store/slices/resourcesSlice';
import { selectFavorites } from '@/store/selectors/resourcesSelectors';
import { selectUserType } from '@/store/selectors/authSelectors';

interface IProps {
  postedDate?: string;
  onClose: () => void;
  data: IResourceItemType | IResourceDatabaseRecord;
}

const resourcesPreviewHeaderSelector = createSelector(
  selectFavorites,
  selectUserType,
  (
    _: RootState,
    { item, selectedFolder }: { item: { id: number; type: IResourceType }; selectedFolder: ISelectedFolder },
  ) => ({
    item,
    selectedFolder,
  }),
  (favorites, userType, { item: { id, type }, selectedFolder }) => ({
    favorites,
    userType,
    isFavorite:
      (favorites && type !== 'database' && (favorites[ResourceTypeId[type]] as number[])?.includes(id)) ||
      (favorites &&
        type === 'database' &&
        selectedFolder &&
        (favorites[ResourceTypeId[type]] as Record<number, number[]>)[selectedFolder.folderId]?.includes(id)),
  }),
);

const ResourcePreviewHeader = ({ onClose, postedDate, data }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const dispatch = useAppDispatch();
  const { selectedFolder } = useResourceNavigation();
  const { favorites, isFavorite, userType } = useAppSelector(state =>
    resourcesPreviewHeaderSelector(state, { item: getResourceTypeId(data), selectedFolder }),
  );

  useEffect(() => {
    if (favorites === null) {
      const initCall = dispatch(getFavoritesList());
      return () => initCall.abort();
    }
    return;
  }, [dispatch, favorites]);

  const formattedDate = useMemo(() => {
    if (!postedDate) {
      return '';
    }

    return formatDateTime(postedDate, MONTH_DAY_YEAR_HOUR_FORMAT);
  }, [formatDateTime, postedDate]);

  const handleToggleFavorite = () => {
    const { type, id } = getResourceTypeId(data);
    const options: IResourceFavoriteRequest = {
      resourceTypeId: ResourceTypeId[type],
      resourceItemId: id,
      ...(type === 'database' && { resourceClassId: selectedFolder?.folderId }),
    };

    if (isFavorite) {
      dispatch(removeFavorite(options));
    } else {
      dispatch(setFavorite(options));
    }
  };

  return (
    <Container>
      <CustomButtonBack onClick={onClose} />
      <div>
        <span className="date-title">{t`resources.foldersList.headerPostedDate`}:</span>
        <span className="date">{formattedDate || t`common.labelNotAvailable`}</span>
      </div>
      <div className="action-container">
        {favorites !== null && userType === UserType.agent && (
          <div className="action" onClick={handleToggleFavorite}>
            <StarIcon src={isFavorite ? Star : StarEmpty} />
            <span>{t`resources.files.favourite`}</span>
          </div>
        )}
      </div>
      <CloseIcon onClick={onClose} />
    </Container>
  );
};

export { ResourcePreviewHeader };

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;

  .date-title {
    font-size: 12px;
    color: ${colors.gray.c400};
    font-weight: 700;
    margin-right: 8px;
  }
  .date {
    font-size: 12px;
    color: ${colors.gray.c400};
    font-weight: 500;
  }

  .action-container {
    display: flex;
    align-items: center;
    flex: auto;
    flex-direction: row-reverse;
    gap: 12px;
  }

  .action {
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    cursor: pointer;

    span {
      margin-left: 6px;
    }
  }

  .open-new {
    display: none;

    ${media.phone} {
      display: flex;
    }
  }

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    gap: 0;
  }
`;

const StarIcon = styled.img`
  height: 24px;
  width: 24px;
  cursor: pointer;
`;

const CloseIcon = styled.div`
  height: 12px;
  width: 12px;
  display: none;
  margin-left: 42px;

  background-image: url(${CrossIcon});
  background-repeat: no-repeat;
  cursor: pointer;

  ${media.tablet} {
    display: block;
  }
`;

const CustomButtonBack = styled(ButtonBack)`
  width: 65px;
  height: 30px;
  padding: 0;
  border: 1px solid ${otherColors.lxc.blue};
  border-radius: 15px;

  font-size: 12px;
  font-weight: 500;

  svg {
    width: 16px;
    height: 16px;
    margin: 0 !important;
  }

  ${media.tablet} {
    display: none;
  }
`;
